import queryString from "query-string";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { DeliveryTypes, ProductVisibilityTypes } from "@utils/enums";
import { hasWindow } from "@utils/general";
import { once } from "@utils/general";
import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { getProductConfiguration } from "@utils/productConfiguration/productConfiguration.utils";
import { getPreferredStoreCodeForStock } from "@utils/store";

import { addProductToRecentlyViewed } from "@services/recentlyViewedProducts.service";
import {
    EventTypes,
    ProductImpressionModel,
    trackProductImpression,
} from "@services/tracking";

import { useThunkDispatch } from "@redux/dispatcher";
import { getBoxCount } from "@redux/modules/api/data/boxCount/boxCount.selectors";
import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";
import {
    APIStatus,
    addToCart,
    load as loadCart,
    updateCartItemConfig,
} from "@redux/modules/api/data/cart/cart.module";
import { getCart } from "@redux/modules/api/data/cart/cart.selectors";
import { CartItemConfigUpdateOrigin } from "@redux/modules/api/data/cart/cart.types";
import { load as loadCustomer } from "@redux/modules/api/data/customer/customer.module";
import { getPreferredStoreByCode } from "@redux/modules/api/data/preferredStore/preferredStore.module";
import {
    getPreferredStore,
    hasError,
} from "@redux/modules/api/data/preferredStore/preferredStore.selectors";
import { clear as clearMessages } from "@redux/modules/value/apiMessages/apiMessages.module";
import { broadcastMessage } from "@redux/modules/value/messages/messages.utils";
import { set as setProductComparisonSkuList } from "@redux/modules/value/productComparisonSkuList/productComparisonSkuList.module";
import { getProductComparisonSkuList } from "@redux/modules/value/productComparisonSkuList/productComparisonSkuList.selectors";
import {
    getDeliveryType,
    set,
} from "@redux/modules/value/productDetail/productDetail.module";
import { getProductDetail } from "@redux/modules/value/productDetail/productDetail.selectors";
import { TProductDetailState } from "@redux/modules/value/productDetail/productDetail.types";
import { useGranite } from "@redux/modules/value/translations/translations.provider";
import { TApiAction } from "@redux/store.types";

import { EcommerceComponent } from "@components/Product/ECommerce/eCommerce.component";
import {
    broadcastAddToCartNotAvailableForServiceProduct,
    isAdditionalService,
} from "@components/Product/ECommerce/eCommerce.helpers";

import { getProductTrackingInfo } from "@factory/Tracking/product.model";

import { ECommerceModel, TooltipType } from "./eCommerce.types";

export type EcommerceContainerProps = {
    ecommerceProduct: ECommerceModel;
    tooltips: {
        homeDelivery: TooltipType;
        pickupInStore: TooltipType;
        serviceProduct: TooltipType;
    };
    infoLink: string;
};

const ECommerceContainer = ({
    ecommerceProduct,
    tooltips,
    infoLink,
}: EcommerceContainerProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const shoppingCart = useSelector(getCart);
    const [initialised, setInitialised] = useState(false);
    const [isBoxSoldOut, setIsBoxSoldOut] = useState(false);
    const [configuration, setConfiguration] = useState<Record<string, unknown>>(
        {},
    );
    const [deliveryTypesDisabled, setDeliveryTypesDisabled] = useState(true);
    const productDetail = useSelector(getProductDetail);
    const preferredStore = useSelector(getPreferredStore);
    const productComparisonSkuList = useSelector(getProductComparisonSkuList);
    const hasPreferredStoreErrors = useSelector(hasError);
    const boxCount = useSelector(getBoxCount(ecommerceProduct.sku));
    const isClient = useIsClientSide();

    const qpCartEntry = queryString.parse(!hasWindow() ? "" : location.search)
        ?.cartid as string;
    const cartProduct = shoppingCart?.items.find(i => i.id === qpCartEntry);

    const productConfiguration = React.useMemo(
        () => getProductConfiguration(productDetail.sku),
        [productDetail.sku],
    );

    const updateProductDetail = (data: Partial<TProductDetailState>) =>
        Promise.resolve(dispatch(set(data)));

    const isDSVOnly = (ecommerceProduct: ECommerceModel) =>
        ecommerceProduct.deliveryTypes.length === 1 &&
        ecommerceProduct.deliveryTypes[0] === DeliveryTypes.HomeDelivery;

    React.useEffect(() => {
        addProductToRecentlyViewed(ecommerceProduct.sku);

        dispatch(loadCart());
        dispatch(loadCustomer());

        let deliveryTypeToSet = getDeliveryType();
        let quantityToSet = Math.max(productDetail?.quantity || 0, 1);

        if (deliveryTypeToSet !== DeliveryTypes.ClickAndCollect) {
            quantityToSet = ecommerceProduct.minimumOrderQuantity;
        }

        const updateDeliveryTypePromise = updateProductDetail({
            deliveryType: deliveryTypeToSet as DeliveryTypes,
            quantity: quantityToSet,
            sku: ecommerceProduct.sku,
        });

        const promises = [updateDeliveryTypePromise];

        const storeId = getPreferredStoreCodeForStock();
        if (storeId && !isDSVOnly(ecommerceProduct)) {
            const preferredStorePromise = dispatch(
                getPreferredStoreByCode({
                    code: storeId,
                    sku: ecommerceProduct.sku,
                    includeProductAvailability: true,
                }),
            );
            promises.push(preferredStorePromise);
        }

        Promise.all(promises).then(() => {
            setDeliveryTypesDisabled(false);
            setInitialised(true);
        });
    }, []);

    React.useEffect(() => {
        once(() =>
            trackProductImpression(
                getProductTrackingInfo({
                    product: {
                        ean: ecommerceProduct.ean,
                        productTitle: ecommerceProduct.product.productTitle,
                        sku: ecommerceProduct.sku,
                        analytics: ecommerceProduct.product.analytics,
                        brandName: ecommerceProduct.product.brand?.name,
                        currentPrice:
                            ecommerceProduct.product.price?.currentPrice,
                        roundedDiscountPercentage:
                            ecommerceProduct.product.price
                                ?.roundedDiscountPercentage,
                    },
                }),
                EventTypes.ProductDetailImpression,
            ),
        )();
    }, []);

    React.useEffect(() => {
        if (
            !cartProduct ||
            !cartProduct.configuration ||
            !Object.keys(cartProduct.configuration || {})
        ) {
            return;
        }

        updateProductDetail({
            quantity: cartProduct.quantity,
            deliveryType: cartProduct.deliveryType,
        });
        setConfiguration(cartProduct.configuration);
    }, [cartProduct]);

    React.useEffect(() => {
        setIsBoxSoldOut(
            ecommerceProduct?.product?.boxArticle &&
                boxCount !== undefined &&
                boxCount <= 0,
        );
    }, [boxCount]);

    const onQuantityChange = (amount: number, max?: number, min?: number) => {
        let newQuantity = amount;

        if (max !== undefined && amount > max) {
            newQuantity = max;
        } else if (min !== undefined && amount < min) {
            newQuantity = min;
        }
        updateProductDetail({ quantity: newQuantity });
    };

    const onSetProductComparisonSku = (skuList: string[]) => {
        dispatch(setProductComparisonSkuList(skuList));
    };

    const getSubmitDispatch = (store: string | null) => {
        if (productConfiguration && cartProduct) {
            return dispatch(
                updateCartItemConfig(
                    cartProduct.id!,
                    productDetail.quantity,
                    productDetail.deliveryType,
                    configuration,
                    cartProduct,
                    productConfiguration.productSubType,
                    CartItemConfigUpdateOrigin.PRODUCT_PAGE,
                    store || shoppingCart?.storeId,
                ),
            );
        }

        return dispatch(
            addToCart(
                {
                    deliveryType: productDetail.deliveryType,
                    quantity: productDetail.quantity,
                    shopId: store,
                    sku: productDetail.sku,
                    additionalServices: productDetail.serviceProductSkus,
                },
                ecommerceProduct,
                true,
                productConfiguration?.productSubType,
                configuration,
            ),
        );
    };

    const onSubmit = (store: string | null) => {
        if (isAdditionalService(ecommerceProduct)) {
            broadcastAddToCartNotAvailableForServiceProduct(t);
            return;
        }

        const previousStore = shoppingCart?.storeId;

        getSubmitDispatch(store).then(action => {
            if (action.type === APIStatus.SUCCESS) {
                location.hash = "add-to-cart";
                determineWarningMessage(action.payload);
            } else if (action.type === APIStatus.FAILURE) {
                broadcastMessage({
                    id: "pdp-error",
                    text: action.payload.message,
                    status: "danger",
                    dismissible: true,
                });
            }
        });

        const hasSameCncSkuInCartWithMinimumQuantity = (
            newCartObject: CartModel,
        ) => {
            const sameSkuInCart = shoppingCart?.items?.find(
                item => item.sku === productDetail.sku,
            );

            if (
                sameSkuInCart &&
                sameSkuInCart.deliveryType === DeliveryTypes.ClickAndCollect
            ) {
                const newCartItem = newCartObject.items?.find(
                    item => item.sku === productDetail.sku,
                );
                return (
                    newCartItem?.minimumOrderQuantity &&
                    newCartItem.minimumOrderQuantity > 1 &&
                    newCartItem.roundingValue &&
                    newCartItem.quantity % newCartItem.roundingValue === 0
                );
            }

            return false;
        };

        const determineWarningMessage = (newCartObject: CartModel) => {
            let warningMessage = "";

            if (
                !shoppingCart?.items.some(
                    i => i.deliveryType === DeliveryTypes.ClickAndCollect,
                )
            ) {
                return;
            }

            if (
                previousStore &&
                previousStore !== store &&
                productDetail.deliveryType === DeliveryTypes.ClickAndCollect
            ) {
                warningMessage = t(
                    "product.addtocart.modal.warning.differentStore",
                );
            } else if (
                productDetail.deliveryType === DeliveryTypes.HomeDelivery &&
                hasSameCncSkuInCartWithMinimumQuantity(newCartObject)
            ) {
                warningMessage = t(
                    "product.addtocart.modal.warning.quantityChanged",
                );
            }

            if (warningMessage) {
                broadcastMessage({
                    id: "add-to-cart-modal-messages",
                    text: warningMessage,
                    status: "warning",
                    dismissible: true,
                });
            }
        };
    };

    const onAddToCart = (
        deliveryType: string,
        quantity: number,
        shop: string | null,
        sku: string,
        ecommerceModel: ECommerceModel,
    ) => {
        if (isAdditionalService(ecommerceModel)) {
            broadcastAddToCartNotAvailableForServiceProduct(t);
            return Promise.reject<TApiAction>();
        }

        return dispatch(
            addToCart(
                {
                    deliveryType: deliveryType,
                    quantity: quantity,
                    shopId: shop,
                    sku: sku,
                    additionalServices: productDetail.serviceProductSkus,
                },
                ecommerceModel,
                false,
                productConfiguration?.productSubType,
                configuration,
            ),
        );
    };

    const handleOnEcommerceBlockVisibleChange = (visible: boolean) => {
        const stickyOrderButton = !visible && productCanBeOrdered;

        updateProductDetail({ stickyOrderButton: stickyOrderButton });
    };

    const trackingProduct: ProductImpressionModel = useMemo(
        () => ({
            product: {
                ean: ecommerceProduct.product.ean,
                productTitle:
                    ecommerceProduct.product.productTitle ||
                    ecommerceProduct.product.name,
                sku: ecommerceProduct.product.sku,
                analytics: ecommerceProduct.product.analytics,
                brandName: ecommerceProduct.product.brand?.name,
                currentPrice: ecommerceProduct.product.price?.currentPrice,
                roundedDiscountPercentage: ecommerceProduct.product.price
                    ?.discountPercentage
                    ? ecommerceProduct.product.price?.roundedDiscountPercentage
                    : undefined,
            },
        }),
        [ecommerceProduct.product],
    );

    const productCanBeOrdered = useMemo(() => {
        const visibilityType = ecommerceProduct.visibilityType;

        const noDelivery =
            visibilityType === ProductVisibilityTypes.NotAvailable ||
            visibilityType === ProductVisibilityTypes.TemporaryNotAvailable ||
            visibilityType === ProductVisibilityTypes.Free;

        const onlyAvailableInStore =
            visibilityType === ProductVisibilityTypes.OnlyAvailableInStore;

        return !noDelivery && !onlyAvailableInStore;
    }, [ecommerceProduct.visibilityType]);

    return (
        <EcommerceComponent
            productDetail={productDetail}
            trackingProduct={trackingProduct}
            preferredStore={hasPreferredStoreErrors ? null : preferredStore}
            ecommerceModel={ecommerceProduct}
            productComparisonSkuList={productComparisonSkuList || []}
            onQuantityChange={(amount, min, max) =>
                onQuantityChange(amount, max, min)
            }
            onProductDetailChange={data => updateProductDetail(data)}
            onEcommerceBlockVisibleChange={handleOnEcommerceBlockVisibleChange}
            onConfigurationChange={config =>
                setConfiguration(currentConfig => ({
                    ...currentConfig,
                    ...config,
                }))
            }
            configuration={configuration}
            onSubmit={onSubmit}
            addToCart={onAddToCart}
            cart={shoppingCart}
            isEcommerceReady={initialised}
            tooltips={{
                [DeliveryTypes.HomeDelivery]: tooltips.homeDelivery,
                [DeliveryTypes.ClickAndCollect]: tooltips.pickupInStore,
                serviceProduct: tooltips.serviceProduct,
            }}
            clearMessages={() => dispatch(clearMessages)}
            infoLink={infoLink}
            isSoldOut={isBoxSoldOut}
            deliveryTypesDisabled={deliveryTypesDisabled}
            onSetProductComparisonSkuList={onSetProductComparisonSku}
            isClient={isClient}
            productConfiguration={productConfiguration}
        />
    );
};

export { ECommerceContainer };
